// NodeDateTimeRangeInput.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  DEFAULT_MINUTE_STEP,
  addDays,
  addHoursToTimeKey,
  applyTimeToDate,
  generateTimeOptions,
  getNearestSlotValue,
  isCompleteRawDate,
  toTimeKey,
} from 'node/form/fields/utilities/dateFieldUtils';
import { normalizeDateValue, timestampFromDate, toDatePrecisionLocalDay } from 'utilities/DateTime';
import { clearDates } from 'utilities/Events';

// CSS
import './NodeDateTimeRangeInput.css';

// Components
import DatePicker from 'components/datepicker/DatePicker';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * NodeDateTimeRangeInput
 *
 * Shared datetime range input for schema-driven range fields.
 *
 * @param {object} value - Current field value.
 * @param {Function} onChange - Callback when range value changes.
 * @param {string} className - Optional wrapper class.
 * @param {boolean} hybrid - Whether to use hybrid DatePicker mode.
 * @param {number} minuteStep - Minute increment for time options.
 * @param {boolean} autoFillEnd - Whether to auto-fill end date/time when needed.
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeDateTimeRangeInput = ({
  value,
  onChange,
  className,
  hybrid = true,
  minuteStep = DEFAULT_MINUTE_STEP,
  autoFillEnd = true,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTimeValue, setStartTimeValue] = useState('');
  const [endTimeValue, setEndTimeValue] = useState('');
  const [startInvalid, setStartInvalid] = useState(false);
  const [endInvalid, setEndInvalid] = useState(false);

  // ----- REFS ----- //

  const startRawRef = useRef('');
  const endRawRef = useRef('');

  // ----- UTILITIES ----- //

  /**
   * Builds the value object to be emitted on change, based on the provided 
   * start and end datetimes. If no start datetime is provided, the range is 
   * considered cleared and null.
   * 
   * @param {Date|null} startDateTime - The selected start datetime.
   * @param {Date|null} endDateTime - The selected end datetime.
   * 
   * @returns {object|null} The normalized range value object or null if cleared.
   */

  const buildDateTimeRangeValue = (startDateTime, endDateTime) => {

    // If no start datetime, consider the range cleared (end datetime is irrelevant without a start).
    if (!startDateTime) return clearDates();

    return {
      startDate: timestampFromDate(startDateTime),
      startPrecision: 'time',
      endDate: endDateTime ? timestampFromDate(endDateTime) : null,
      endPrecision: endDateTime ? 'time' : null,
      isRange: Boolean(endDateTime),
    };
  };

  const emitRangeChange = (nextStartDate, nextStartTime, nextEndDate, nextEndTime) => {
    if (typeof onChange !== 'function') return;

    if (!nextStartDate) {
      onChange(clearDates());
      return;
    }

    const startDateTime = applyTimeToDate(nextStartDate, nextStartTime);
    const endDateTime = nextEndDate ? applyTimeToDate(nextEndDate, nextEndTime) : null;

    onChange(buildDateTimeRangeValue(startDateTime, endDateTime));
  };

  const enforceEndAfterStart = (nextStartDate, nextStartTime, nextEndDate, nextEndTime) => {
    if (!nextStartDate || !nextEndDate) {
      return {
        endDate: nextEndDate,
        endTime: nextEndTime,
      };
    }

    const startDateTime = applyTimeToDate(nextStartDate, nextStartTime);
    const endDateTime = applyTimeToDate(nextEndDate, nextEndTime);
    if (!startDateTime || !endDateTime) {
      return {
        endDate: nextEndDate,
        endTime: nextEndTime,
      };
    }

    if (endDateTime >= startDateTime) {
      return {
        endDate: nextEndDate,
        endTime: nextEndTime,
      };
    }

    // Keep the end on the start date, one hour later (roll to next day only if needed).
    const { timeKey: endTime, dayOffset } = addHoursToTimeKey(nextStartTime, 1);
    return {
      endDate: addDays(nextStartDate, dayOffset),
      endTime,
    };
  };

  const defaultEndFromStart = (nextStartDate, nextStartTime) => {
    const { timeKey: endTime, dayOffset } = addHoursToTimeKey(nextStartTime, 1);
    return {
      endDate: addDays(nextStartDate, dayOffset),
      endTime,
    };
  };

  // ----- MEMOS ----- //

  // Generate time options based on the provided minute step for use in the time select inputs.

  const timeOptions = useMemo(() => generateTimeOptions(minuteStep), [minuteStep]);

  // Determine the resolved time values for start and end based on the current selection 
  // and available options.
  const startResolvedTime = useMemo(() => (
    startTimeValue || getNearestSlotValue(startDate, timeOptions, minuteStep)
  ), [startTimeValue, startDate, timeOptions, minuteStep]);

  // For the end time, if auto-fill is enabled and there's a start date but no end date,
  // we can use the start time as the resolved end time to ensure a complete datetime range.
  const endResolvedTime = useMemo(() => (
    endTimeValue || getNearestSlotValue(endDate, timeOptions, minuteStep)
  ), [endTimeValue, endDate, timeOptions, minuteStep]);

  // ----- EFFECTS ----- //

  // When the value prop changes, normalize the start and end datetimes and update local state accordingly.

  useEffect(() => {

    // Normalize incoming value to Date objects for the pickers, and determine appropriate time slot values.
    // Date-precision (all-day) values are often UTC midnight of the civil day — map to local midnight
    // so Mountain/Pacific don't shift the picker to the previous evening.
    const rawStart = normalizeDateValue(value?.startDate);
    const rawEnd = normalizeDateValue(value?.endDate);
    const startIsDateOnly = String(value?.startPrecision || '').toLowerCase() === 'date';
    const endIsDateOnly = String(value?.endPrecision || value?.startPrecision || '').toLowerCase() === 'date';
    const normalizedStart = startIsDateOnly
      ? (toDatePrecisionLocalDay(rawStart) || rawStart)
      : rawStart;
    const normalizedEnd = endIsDateOnly
      ? (toDatePrecisionLocalDay(rawEnd) || rawEnd)
      : rawEnd;

    // If the incoming value has a start date but no end date, and auto-fill is enabled, we can
    // auto-fill the end date to ensure a complete range for the user.
    setStartDate(normalizedStart);
    setEndDate(normalizedEnd);

    // If the normalized start or end datetimes match exact time options, use those for the time select values.
    if (normalizedStart) {
      if (startIsDateOnly) {
        // All-day: don't surface a misleading local clock time from the UTC instant.
        setStartTimeValue('');
      } else {
        const exactStartTime = toTimeKey(normalizedStart);
        setStartTimeValue(timeOptions.some((opt) => opt.value === exactStartTime)
          ? exactStartTime
          : getNearestSlotValue(normalizedStart, timeOptions, minuteStep));
      }
    } else {
      setStartTimeValue('');
    }

    // For the end time, if auto-fill is enabled and there's a start date but no end date, we can 
    // use the start time as the resolved end time to ensure a complete datetime range. Otherwise, 
    // resolve based on the normalized end value.
    if (normalizedEnd) {
      if (endIsDateOnly) {
        setEndTimeValue('');
      } else {
        const exactEndTime = toTimeKey(normalizedEnd);
        setEndTimeValue(timeOptions.some((opt) => opt.value === exactEndTime)
          ? exactEndTime
          : getNearestSlotValue(normalizedEnd, timeOptions, minuteStep));
      }
    } else {
      setEndTimeValue('');
    }
  }, [value, timeOptions, minuteStep]);

  // ----- HANDLERS ----- //

  /**
   * Handles changes to the start date, applying auto-fill logic for the end date if enabled,
   * and emitting the normalized range value.
   * 
   * @param {Date|null} nextStartDate - The next start date selected by the user.
   */

  const handleStartRawChange = (rawValue) => {
    startRawRef.current = rawValue || '';
    if (!isCompleteRawDate(rawValue)) {
      setStartInvalid(false);
    }
  };

  /**
   * Handles raw input changes for the end date, updating the corresponding ref and 
   * validation state.
   *
   * @param {string} rawValue - The raw input value from the end date picker.
   */
  const handleEndRawChange = (rawValue) => {
    endRawRef.current = rawValue || '';
    if (!isCompleteRawDate(rawValue)) {
      setEndInvalid(false);
    }
  };

  /**
   * Handles changes to the start date, applying auto-fill logic for the end date if enabled,
   * and emitting the normalized range value.
   *
   * @param {Date|null} nextStartDate - The next start date selected by the user.
   */
  const handleStartDateChange = (nextStartDate) => {
    setStartDate(nextStartDate);

    if (!nextStartDate) {
      const rawValue = startRawRef.current || '';
      if (!rawValue) {
        setStartInvalid(false);
        setEndDate(null);
        setEndTimeValue('');
        setEndInvalid(false);
        emitRangeChange(null, '', null, '');
        return;
      }

      if (isCompleteRawDate(rawValue)) {
        setStartInvalid(true);
      }
      return;
    }

    setStartInvalid(false);

    const nextStartTime = startResolvedTime;
    let nextEndDate = endDate;
    let nextEndTime = endResolvedTime;

    if (autoFillEnd && !nextEndDate) {
      const defaults = defaultEndFromStart(nextStartDate, nextStartTime);
      nextEndDate = defaults.endDate;
      nextEndTime = defaults.endTime;
    }

    const normalized = enforceEndAfterStart(nextStartDate, nextStartTime, nextEndDate, nextEndTime);

    setEndDate(normalized.endDate || null);
    setEndTimeValue(normalized.endTime || '');

    emitRangeChange(nextStartDate, nextStartTime, normalized.endDate || null, normalized.endTime || '');
  };

  /**
   * Handles changes to the end date, ensuring it cannot be set before the start date, and emitting the
   * normalized range value.
   *
   * @param {Date|null} nextEndDate - The next end date selected by the user.
   */
  const handleEndDateChange = (nextEndDate) => {
    if (!startDate) return;

    setEndDate(nextEndDate);

    if (!nextEndDate) {
      const rawValue = endRawRef.current || '';
      if (!rawValue) {
        setEndInvalid(false);
        if (autoFillEnd) {
          const fallback = defaultEndFromStart(startDate, startResolvedTime);
          setEndDate(fallback.endDate);
          setEndTimeValue(fallback.endTime);
          emitRangeChange(startDate, startResolvedTime, fallback.endDate, fallback.endTime);
        } else {
          setEndTimeValue('');
          emitRangeChange(startDate, startResolvedTime, null, '');
        }
        return;
      }

      if (isCompleteRawDate(rawValue)) {
        setEndInvalid(true);
      }
      return;
    }

    setEndInvalid(false);

    const nextEndTime = endResolvedTime;
    const normalized = enforceEndAfterStart(startDate, startResolvedTime, nextEndDate, nextEndTime);
    setEndDate(normalized.endDate || null);
    setEndTimeValue(normalized.endTime || '');

    emitRangeChange(startDate, startResolvedTime, normalized.endDate || null, normalized.endTime || '');
  };

  /**
   * Handles changes to the start time, ensuring the end datetime remains valid if a start date is 
   * selected, and emitting the normalized range value.
   *
   * @param {Event} event - The change event from the start time input.
   */
  const handleStartTimeChange = (event) => {
    const nextStartTime = event.target.value;
    setStartTimeValue(nextStartTime);

    if (!startDate) return;

    const normalized = enforceEndAfterStart(startDate, nextStartTime, endDate, endResolvedTime);
    setEndDate(normalized.endDate || null);
    setEndTimeValue(normalized.endTime || '');

    emitRangeChange(startDate, nextStartTime, normalized.endDate || null, normalized.endTime || '');
  };

  /**
   * Handles changes to the end time, ensuring it cannot create an end datetime before the start 
   * datetime, and emitting the normalized range value.
   * 
   * @param {Event} event - The change event from the end time input.
   */
  const handleEndTimeChange = (event) => {
    const nextEndTime = event.target.value;
    setEndTimeValue(nextEndTime);
    if (!startDate || !endDate) return;

    const normalized = enforceEndAfterStart(startDate, startResolvedTime, endDate, nextEndTime);
    setEndDate(normalized.endDate || null);
    setEndTimeValue(normalized.endTime || '');

    emitRangeChange(startDate, startResolvedTime, normalized.endDate || null, normalized.endTime || '');
  };

  // ----- RENDER ----- //

  return (
    <div className="node-date-time-range-input-container">

      {/* ROW */}
      <div className="node-date-time-range-row">

        {/* LABEL */}
        <div
          className="node-date-time-range-label"
        >Start</div>

        {/* COLUMN */}
        <div className="node-date-time-range-column">

          {/* PICKER */}
          <DatePicker
            className="node-date-time-range-picker"
            value={startDate}
            inputClassName={startInvalid ? 'date-picker-invalid' : ''}
            startIconKey="calendar_month"
            startIconColor={theme.foregroundMuted}
            iconSize={22}
            hybrid={hybrid}
            onChange={handleStartDateChange}
            onRawChange={handleStartRawChange}
          />

          {/* TIME SELECT */}
          {startDate && (

            <div className="node-date-time-range-time">

              <SelectInput
                value={startResolvedTime}
                options={timeOptions}
                hybrid={hybrid}
                onChange={handleStartTimeChange}
              />

            </div>

          )}

        </div>

      </div>

      {/* ROW */}
      <div className="node-date-time-range-row">

        {/* LABEL */}
        <div
          className="node-date-time-range-label"
        >End</div>

        {/* COLUMN */}
        <div className="node-date-time-range-column">

          {/* PICKER */}
          <DatePicker
            className="node-date-time-range-picker"
            value={endDate}
            inputClassName={endInvalid ? 'date-picker-invalid' : ''}
            startIconKey="calendar_month"
            startIconColor={theme.foregroundMuted}
            iconSize={22}
            hybrid={hybrid}
            disabled={!startDate}
            onChange={handleEndDateChange}
            onRawChange={handleEndRawChange}
          />

          {/* TIME SELECT */}
          {startDate && endDate && (

            <div className="node-date-time-range-time">

              <SelectInput
                value={endResolvedTime}
                options={timeOptions}
                hybrid={hybrid}
                onChange={handleEndTimeChange}
              />

            </div>

          )}

        </div>

      </div>

    </div >
  );
};

// Dev Tools
NodeDateTimeRangeInput.displayName = 'NodeDateTimeRangeInput';

export default NodeDateTimeRangeInput;
